/**
 * useActiveSessions Hook — "Where you're signed in", on the web portals and
 * the phone.
 *
 * Fetches all active sessions for the current user via Clerk's
 * `user.getSessions()` API. Provides real device/browser info,
 * relative timestamps, and the ability to revoke (sign out) sessions. How an
 * outcome is told (a toast) is the client's (`notify`).
 *
 * @see https://clerk.com/docs/reference/javascript/types/session-with-activities
 */

import { useState, useEffect, useCallback } from 'react';
import { useUser, useSession } from '@clerk/clerk-react';
import type { SessionWithActivitiesResource } from '@clerk/types';
import { useTranslation, useLocale, formatDate as formatLocaleDate } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';

import type { AccountNotifier } from './notifier';

// ============================================================================
// TYPES
// ============================================================================

export interface ActiveSession {
  /** Clerk session id */
  id: string;
  /** e.g. "Chrome 120" */
  browser: string;
  /** e.g. "macOS", "Windows", "iPhone", "Android" */
  device: string;
  /** e.g. "Lisbon, PT" or "Unknown location" */
  location: string;
  /** IP address */
  ipAddress: string | null;
  /** Whether this is the current active session */
  isCurrent: boolean;
  /** Whether this session is on a mobile device */
  isMobile: boolean;
  /** Human-readable relative time, e.g. "5 minutes ago" */
  lastActive: string;
  /** Raw Date for sorting */
  lastActiveAt: Date;
  /** Session status from Clerk */
  status: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Minimal translate-function shape (module helpers receive `t` from the hook). */
type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Format a Date into a human-readable relative string.
 */
function formatRelativeTime(date: Date, t: TFn, intlLocale: string): string {
  const now = Date.now();
  const diff = now - date.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return t('common.relativeTime.justNow');
  if (minutes < 60) return t('common.relativeTime.minutesAgo', { count: minutes });
  if (hours < 24) return t('common.relativeTime.hoursAgo', { count: hours });
  if (days < 7) return t('common.relativeTime.daysAgo', { count: days });

  // The app locale (`useLocale().intlLocale`), not the browser's (F34).
  return formatLocaleDate(date, intlLocale, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Build a friendly device label from Clerk's SessionActivity.
 */
function buildDeviceLabel(
  activity: SessionWithActivitiesResource['latestActivity'],
  t: TFn
): string {
  if (!activity) return t('common.device.unknown');
  return (
    activity.deviceType ||
    (activity.isMobile ? t('common.device.mobile') : t('common.device.desktop'))
  );
}

/**
 * Build a friendly browser label from Clerk's SessionActivity.
 */
function buildBrowserLabel(
  activity: SessionWithActivitiesResource['latestActivity'],
  t: TFn
): string {
  if (!activity) return t('common.browser.unknown');
  const name = activity.browserName || t('common.browser.unknown');
  const version = activity.browserVersion ? ` ${activity.browserVersion}` : '';
  return `${name}${version}`;
}

/**
 * Build a location string from Clerk's SessionActivity.
 */
function buildLocation(activity: SessionWithActivitiesResource['latestActivity'], t: TFn): string {
  if (!activity) return t('common.location.unknown');
  const parts: string[] = [];
  if (activity.city) parts.push(activity.city);
  if (activity.country) parts.push(activity.country);
  return parts.length > 0 ? parts.join(', ') : t('common.location.unknown');
}

/**
 * Map a Clerk SessionWithActivitiesResource to our ActiveSession type.
 */
function mapSession(
  raw: SessionWithActivitiesResource,
  currentSessionId: string | undefined,
  t: TFn,
  intlLocale: string
): ActiveSession {
  const activity = raw.latestActivity;

  return {
    id: raw.id,
    browser: buildBrowserLabel(activity, t),
    device: buildDeviceLabel(activity, t),
    location: buildLocation(activity, t),
    ipAddress: activity?.ipAddress ?? null,
    isCurrent: raw.id === currentSessionId,
    isMobile: activity?.isMobile ?? false,
    lastActive: formatRelativeTime(raw.lastActiveAt, t, intlLocale),
    lastActiveAt: raw.lastActiveAt,
    status: raw.status,
  };
}

// ============================================================================
// HOOK
// ============================================================================

export function useActiveSessions({ notify }: { notify: AccountNotifier }) {
  const { user } = useUser();
  const { session: currentSession } = useSession();
  const { captureException } = useErrorCapture();
  const { t } = useTranslation();
  // A stable string, so the fetch callback below is not rebuilt every render.
  const { intlLocale } = useLocale();

  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch sessions ──────────────────────────────────────────────────
  const fetchSessions = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      const rawSessions = await user.getSessions();

      // Filter to only active sessions and sort current-first, then by lastActiveAt desc
      const mapped = rawSessions
        .filter((s) => s.status === 'active')
        .map((s) => mapSession(s, currentSession?.id, t, intlLocale))
        .sort((a, b) => {
          // Current session always first
          if (a.isCurrent && !b.isCurrent) return -1;
          if (!a.isCurrent && b.isCurrent) return 1;
          // Then by most recent activity
          return b.lastActiveAt.getTime() - a.lastActiveAt.getTime();
        });

      setSessions(mapped);
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'use-active-sessions',
        action: 'fetchSessions',
      });
      setError(t('settings.sessions.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [user, currentSession?.id, captureException, t, intlLocale]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // ── Revoke a session ────────────────────────────────────────────────
  const revokeSession = useCallback(
    async (sessionId: string) => {
      if (!user) return;

      try {
        const rawSessions = await user.getSessions();
        const target = rawSessions.find((s) => s.id === sessionId);

        if (!target) {
          notify.error(t('settings.sessions.notFound'));
          return;
        }

        await target.revoke();
        notify.success(t('settings.sessions.signedOut'));

        // Refresh the session list
        await fetchSessions();
      } catch (err) {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'use-active-sessions',
          action: 'revokeSession',
        });
        notify.error(t('settings.sessions.signOutFailed'));
      }
    },
    [user, fetchSessions, captureException, t, notify]
  );

  // ── Sign out of every other device ──────────────────────────────────
  const revokeOthers = useCallback(async () => {
    if (!user) return;
    try {
      const rawSessions = await user.getSessions();
      await Promise.all(
        rawSessions
          .filter((s) => s.status === 'active' && s.id !== currentSession?.id)
          .map((s) => s.revoke())
      );
      notify.success(t('settings.sessions.signedOut'));
      await fetchSessions();
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'use-active-sessions',
        action: 'revokeOthers',
      });
      notify.error(t('settings.sessions.signOutFailed'));
    }
  }, [user, currentSession?.id, fetchSessions, captureException, t, notify]);

  return {
    sessions,
    isLoading,
    error,
    revokeSession,
    revokeOthers,
    refreshSessions: fetchSessions,
  };
}
